import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { CoreActions } from '@next/core-lib';
import { DialogService, DIALOG_RETURN_TYPES } from '@next/core-lib/dialog';
import { LocaleService } from '@next/core-lib/i18n';
import { NotificationActions } from '@next/core-lib/notification';
import { SettingsService, SettingsStorage } from '@next/core-lib/settings';
// @ts-ignore Ignore unused commented imports
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { combineLatest, EMPTY, Observable, of } from 'rxjs';
import {
  catchError,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  startWith,
  switchMap,
  take,
  tap,
  withLatestFrom,
} from 'rxjs/operators';

import { LayerContextKeys } from '../../../shared/constants';
import { Product } from '../../../shared/models';
import { ProductModuleState } from '../../models/product.model';
import {
  ProductIsInUseResponse,
  UpsertProductResponse,
} from '../../services/product-service.model';
import { ProductService } from '../../services/product.service';
// @ts-ignore Ignore unused commented ActionTypes import
import * as actions from '../actions/product.actions';
import { getSelectedCountry } from '../selectors/product.selectors';

@Injectable()
export class ProductEffects {
  constructor(
    private readonly actions$: Actions,
    private ngZone: NgZone,
    private router: Router,
    private readonly store: Store<ProductModuleState>,
    private localeService: LocaleService,
    private productService: ProductService,
    private dialogService: DialogService,
    private settingsService: SettingsService,
  ) {}

  loadData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadData),
      exhaustMap(() => of(actions.clearProductState(), actions.loadSelectedCountryRequest())),
    ),
  );

  loadSelectedCountrySuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadSelectedCountrySuccess),
      map(() => actions.loadProductsRequest()),
    ),
  );

  loadProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadProductsRequest),
      withLatestFrom(this.store.select(getSelectedCountry)),
      filter(([_, country]) => country !== undefined),
      exhaustMap(([_action, _country]) =>
        this.productService.getProducts().pipe(
          switchMap((products: Product[]) => of(actions.loadProductsSuccess({ products }))),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadProductsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  loadSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.loadSelectedCountryRequest),
      exhaustMap((_) =>
        this.settingsService
          .getSingle(
            {
              contextName: LayerContextKeys.POULTRYNEXTLAYER,
              key: LayerContextKeys.PRODUCTS_COUNTRY,
              storage: SettingsStorage.Global,
              basedOnCurrentLocation: true,
            },
            'BE',
          )
          .pipe(
            switchMap((settings) => of(actions.loadSelectedCountrySuccess({ id: settings }))),
            catchError((error: HttpErrorResponse) =>
              of(
                actions.loadProductsError({ error }),
                NotificationActions.errorSnackbar({ message: error.message }),
              ),
            ),
            startWith(CoreActions.setLoading({ loading: true })),
            finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
          ),
      ),
    ),
  );

  setSelectedCountry$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setSelectedCountryRequest),
      exhaustMap((id) =>
        this.productService.getProducts().pipe(
          switchMap((products: Product[]) => {
            this.settingsService.setSettings({
              contextName: LayerContextKeys.POULTRYNEXTLAYER,
              key: LayerContextKeys.PRODUCTS_COUNTRY,
              storage: SettingsStorage.Global,
              basedOnCurrentLocation: true,
              data: id.id,
            });

            return of(
              actions.setSelectedCountrySuccess({
                id: id.id,
                products: products.filter((x) => x?.country === id.id),
              }),
            );
          }),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.loadProductsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  setSelectedProduct$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.setSelectedProductRequest),
      exhaustMap(({ id }) =>
        this.productService.getProductById(id).pipe(
          switchMap((product: Product) => of(actions.setSelectedProductSuccess({ product }))),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.setSelectedProductError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
        ),
      ),
    ),
  );

  getProducts$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.getProductsRequest),
      mergeMap(() =>
        this.productService.getProducts().pipe(
          switchMap((products: Product[]) => of(actions.getProductsSuccess({ products }))),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.getProductsError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertProduct$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.upsertProductRequest),
      mergeMap((request) =>
        this.productService.upsertProduct(request.request).pipe(
          switchMap((response: UpsertProductResponse) =>
            of(
              actions.upsertProductSuccess({ response }),
              actions.loadProductsRequest(),
              NotificationActions.successSnackbar({
                message: this.localeService.translate('product-saved'),
              }),
            ),
          ),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.upsertProductError({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  upsertProductSuccess$: Observable<UpsertProductResponse> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.upsertProductSuccess),
        map((action) => action.response),
        tap((_) => {
          this.ngZone.run(() => this.router.navigate(['/product'])).then();
        }),
      ),
    { dispatch: false },
  );

  deleteProductWithConfirmation$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductWithConfirmation),
      exhaustMap(({ productId }) =>
        combineLatest([this.productService.checkProductIsInUse(productId), of(productId)]).pipe(
          take(1),
        ),
      ),
      switchMap(([response, productId]) =>
        this.dialogService
          .confirmWarning({
            title: this.localeService.translate('product-overview.delete-confirmation-title'),
            text: (response as ProductIsInUseResponse).isInUse
              ? this.localeService.translate(
                  `product-overview.delete-confirmation-message-when-in-use`,
                )
              : this.localeService.translate(`product-overview.delete-confirmation-message`),
          })
          .pipe(
            switchMap(
              (result) =>
                (result.status === DIALOG_RETURN_TYPES.confirm && [
                  actions.deleteProductRequest({
                    productId,
                  }),
                ]) ||
                EMPTY,
            ),
          ),
      ),
    ),
  );

  deleteProductRequest$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.deleteProductRequest),
      mergeMap(({ productId }) =>
        this.productService.deleteProduct(productId).pipe(
          map(() => actions.deleteProductSuccess({ productId })),
          catchError((error: HttpErrorResponse) =>
            of(
              actions.deleteProductFailed({ error }),
              NotificationActions.errorSnackbar({ message: error.message }),
            ),
          ),
          startWith(CoreActions.setLoading({ loading: true })),
          finalize(() => this.store.dispatch(CoreActions.setLoading({ loading: false }))),
        ),
      ),
    ),
  );

  deleteProductSucces$: Observable<Action> = createEffect(
    () =>
      this.actions$.pipe(
        ofType(actions.deleteProductSuccess),
        tap(() => {
          this.ngZone.run(() => this.router.navigate(['/product'])).then();
        }),
      ),
    { dispatch: false },
  );
}
